// Core types for multi-instance architecture

export interface InstanceContext {
  id: string
  name: string
  environment: 'development' | 'staging' | 'production'
  team?: string
  service?: string
  metadata: Record<string, unknown>
}

export interface MiharuInstance {
  readonly id: string
  readonly config: import('./config').MiharuConfig
  readonly context: InstanceContext | undefined
  
  // Core methods
  init(): Promise<void>
  shutdown(): Promise<void>
  disable(): void
  
  // Provider management
  patchProvider(provider: string, client: unknown): Promise<PatchResult>
  
  // Statistics and metrics
  getStats(): Promise<InstanceStats>
  getMetrics(): InstanceMetrics
  
  // Internal methods for updating stats (used by patchers)
  incrementCallCount(): void
  addCost(cost: number): void
  addTokens(tokens: number): void
  incrementErrors(): void
  incrementBatchesProcessed(): void
  
  // Trace management
  addTrace(trace: TraceData): void
  removeTrace(traceId: string): void
  getTrace(traceId: string): TraceData | undefined
}

export interface PatchResult {
  success: boolean
  method: 'monkey-patch' | 'wrapper' | 'already-patched' | 'multi-strategy' | 'openai-patch' | 'anthropic-patch' | 'google-patch'
  strategy?: string
  healthCheck?: boolean
  error?: string
}

export interface InstanceStats {
  totalCalls: number
  totalCost: number
  totalTokens: number
  batchesProcessed: number
  errors: number
  uptime: number
}

export interface InstanceMetrics {
  instanceId: string
  context: InstanceContext | undefined
  activeTraces: number
  patchedClients: number
  memoryUsage: NodeJS.MemoryUsage
  uptime: number
}

// Trace and session types
export interface TraceData {
  id: string
  session_id: string
  parent_id: string | null
  start_time: Date
  end_time: Date | null
  status: 'running' | 'completed' | 'failed'
  metadata: {
    user_id?: string
    environment?: string
    version?: string
    instance_id?: string
    custom?: Record<string, unknown>
  }
}

export interface Session {
  id: string
  startTime: number
  lastActivity: number
  callCount: number
  totalCost: number
  totalTokens: number
  metadata: {
    userId?: string
    environment?: string
    detectionMethod?: string
    [key: string]: unknown
  }
}

// LLM Call types with provider-specific data
export interface LLMCallData {
  id: string
  trace_id: string
  provider: 'openai' | 'anthropic' | 'google'
  model: string
  input_tokens: number
  output_tokens: number
  total_tokens: number
  cost: number
  latency_ms: number
  created_at: Date
  pricing_version: string
  request_data: unknown
  response_data: unknown
  is_streaming?: boolean
  stream_chunks?: number
  stream_interrupted?: boolean
}

// Error types
export interface ErrorData {
  id: string
  trace_id: string
  llm_call_id?: string
  error_type: 'HTTP_4XX' | 'HTTP_5XX' | 'TIMEOUT' | 'NETWORK' | 'RATE_LIMIT' | 'VALIDATION' | 'UNKNOWN'
  error_message: string
  error_code?: string
  stack_trace?: string
  retry_count: number
  created_at: Date
}

// Enhanced storage adapter interface with health checks and recovery
export interface StorageAdapter {
  readonly id: string
  readonly type: 'memory' | 'sqlite' | 'supabase'
  
  // Lifecycle management
  init(): Promise<void>
  shutdown(): Promise<void>
  reconnect(): Promise<void>
  
  // Health monitoring
  healthCheck(): Promise<StorageHealthStatus>
  getHealthStatus(): 'healthy' | 'degraded' | 'unhealthy'
  getConnectionInfo(): StorageConnectionInfo
  
  // Data operations
  saveTrace(trace: TraceData): Promise<void>
  saveLLMCall(call: LLMCallData): Promise<void>
  saveError(error: ErrorData): Promise<void>
  saveBatch(items: unknown[]): Promise<BatchSaveResult>
  
  // Query operations with pagination
  getTraces(filter?: TraceFilter, pagination?: PaginationOptions): Promise<PaginatedResult<TraceData>>
  getLLMCalls(filter?: LLMCallFilter, pagination?: PaginationOptions): Promise<PaginatedResult<LLMCallData>>
  getErrors(filter?: ErrorFilter, pagination?: PaginationOptions): Promise<PaginatedResult<ErrorData>>
  
  // Data retention and cleanup
  deleteTracesOlderThan(date: Date): Promise<number>
  deleteLLMCallsOlderThan(date: Date): Promise<number>
  deleteErrorsOlderThan(date: Date): Promise<number>
  getStorageSize(): Promise<StorageSize>
  
  // Backup and recovery
  createBackup(path?: string): Promise<BackupInfo>
  restoreFromBackup(backupPath: string): Promise<void>
  listBackups(): Promise<BackupInfo[]>
  deleteBackup(backupId: string): Promise<void>
  
  // Migration support
  executeSql(sql: string): Promise<void>
  getCurrentVersion(): Promise<string>
  setVersion(version: string): Promise<void>
  
  // Performance monitoring
  getMetrics(): Promise<StorageMetrics>
  resetMetrics(): Promise<void>
}

// Storage supporting types
export interface StorageHealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy'
  lastCheck: Date
  error?: string
  latency?: number
  connectionPool?: {
    active: number
    idle: number
    total: number
  }
  checks: {
    connection: boolean
    write: boolean
    read: boolean
    backup: boolean
  }
}

export interface StorageConnectionInfo {
  host?: string
  database?: string
  pool?: {
    min: number
    max: number
    idle: number
    active: number
  }
  ssl?: boolean
  version?: string
  uptime?: number
}

export interface BatchSaveResult {
  successful: number
  failed: number
  errors: Array<{ index: number; error: string }>
  duration: number
}

export interface PaginationOptions {
  limit?: number
  offset?: number
  orderBy?: string
  orderDirection?: 'ASC' | 'DESC'
}

export interface PaginatedResult<T> {
  data: T[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface TraceFilter {
  session_id?: string
  status?: 'running' | 'completed' | 'failed'
  start_time_from?: Date
  start_time_to?: Date
  metadata?: Record<string, unknown>
}

export interface LLMCallFilter {
  trace_id?: string
  provider?: 'openai' | 'anthropic' | 'google'
  model?: string
  created_at_from?: Date
  created_at_to?: Date
  cost_min?: number
  cost_max?: number
  is_streaming?: boolean
}

export interface ErrorFilter {
  trace_id?: string
  error_type?: string
  created_at_from?: Date
  created_at_to?: Date
  retry_count_min?: number
  retry_count_max?: number
}

export interface StorageSize {
  totalBytes: number
  traces: number
  llmCalls: number
  errors: number
  indexes: number
  overhead: number
}

export interface BackupInfo {
  id: string
  path: string
  size: number
  created_at: Date
  type: 'full' | 'incremental'
  status: 'created' | 'in_progress' | 'completed' | 'failed'
  metadata?: {
    records: number
    version: string
    compression?: string
  }
}

export interface StorageMetrics {
  operations: {
    reads: number
    writes: number
    deletes: number
    errors: number
  }
  performance: {
    averageReadLatency: number
    averageWriteLatency: number
    slowestQuery: number
    totalQueries: number
  }
  storage: {
    totalSize: number
    recordCount: number
    indexSize: number
  }
  uptime: {
    startTime: Date
    totalUptime: number
    lastRestart?: Date
    restartCount: number
  }
}

// Recovery system types
export interface RecoveryTrigger {
  name: string
  condition: () => boolean | Promise<boolean>
  priority: number
  cooldown: number
  maxRetries?: number
}

export interface RecoveryTask {
  id: string
  type: string
  priority: number
  scheduledAt: number
  retryCount: number
  maxRetries: number
  data?: unknown
}

export interface RecoveryHandler {
  canHandle(task: RecoveryTask): boolean
  handle(task: RecoveryTask): Promise<void>
}

export interface RecoverySystemConfig {
  maxConcurrentRecoveries?: number
  checkInterval?: number
  queueMaxSize?: number
  defaultRetries?: number
  retryBackoffMs?: number
  maxRetryBackoffMs?: number
}

// Data retention types
export interface RetentionPolicy {
  name: string
  enabled: boolean
  rules: RetentionRule[]
  schedule: string // cron expression
  dryRun?: boolean
}

export interface RetentionRule {
  type: 'traces' | 'llm_calls' | 'errors' | 'backups'
  condition: RetentionCondition
  action: 'delete' | 'archive' | 'compress'
  batchSize?: number
}

export interface RetentionCondition {
  olderThan?: string // e.g., '30d', '1y'
  status?: string[]
  size?: {
    totalMB?: number
    recordCount?: number
  }
  custom?: (item: unknown) => boolean
}

export interface RetentionResult {
  policyName: string
  executed: Date
  duration: number
  processed: number
  deleted: number
  archived: number
  errors: number
  dryRun: boolean
}

// Provider SDK patching types
export interface SDKPatchStrategy {
  provider: string
  version: string
  patchMethod: 'getter-proxy' | 'method-override' | 'prototype-patch'
  fallbackToWrapper: boolean
  healthCheck: () => boolean
  target: string
  verification: (client: unknown) => boolean
  patchFunction: (client: unknown, wrapper: Function) => PatchResult
}

export interface WrapperInterface {
  wrapClient<T>(originalClient: T): T
  isSupported(client: unknown): boolean
}

// Streaming types
export interface StreamingHandler {
  onStart: (metadata: StreamMetadata) => void
  onChunk: (chunk: unknown, index: number) => void
  onComplete: (summary: StreamSummary) => void
  onError: (error: Error, partialData?: StreamSummary) => void
}

export interface StreamMetadata {
  streamId: string
  startTime: number
  provider: string
  model: string
  requestId?: string
}

export interface StreamSummary {
  inputTokens: number
  outputTokens: number
  totalTokens: number
  chunks: number
  duration: number
  finalContent: string
  interrupted: boolean
  interruptReason?: string
}

// Performance and monitoring types
export interface PerformanceMetrics {
  latency: {
    min: number
    max: number
    mean: number
    median: number
    p95: number
    p99: number
    stdDev: number
  }
  memory: {
    current: number
    peak: number
    gcPauses: number[]
  }
  throughput: {
    requestsPerSecond: number
    tokensPerSecond: number
  }
}

// Error classes
export class MiharuError extends Error {
  public readonly code: string
  public override readonly cause: Error | undefined

  constructor(message: string, code: string, cause?: Error) {
    super(message)
    this.name = 'MiharuError'
    this.code = code
    this.cause = cause
  }

  override toString(): string {
    return `${this.name}: ${this.message} (${this.code})`
  }
}

export class SecurityError extends MiharuError {
  constructor(message: string, code: string, cause?: Error) {
    super(message, code, cause)
    this.name = 'SecurityError'
  }
}